import { orderBy } from 'lodash'
import { reportCategory } from 'src/api'
import { SelectBoxOption } from 'src/components/common/SelectBox/CategorySelectBox'
import {
  CATEGORY_GROUP_NUMBER,
  FILTER_ACCOUNT_ITEM,
  PRIVATE_CATEGORY,
  REPORT_CATEGORY_GROUP
} from 'src/constants'
import { ReportCategory } from 'src/interfaces/api/GetReportCartegories'
import useSWRImmutable from 'swr/immutable'
import { useApiClient } from './useApiClient'

const reportCategorySorter = (
  report_caregories: ReportCategory[]
): ReportCategory[] => {
  const sorted_report_categories = report_caregories.map((v) => {
    return {
      category_type: v.category_type,
      account_items: orderBy(
        v.account_items,
        ['order_no', 'id'],
        ['desc', 'asc']
      )
    }
  })

  return sorted_report_categories
}

const typeValue2CategoryGroupName = (
  typeValue: reportCategory.category_type
) => {
  const targetObject = REPORT_CATEGORY_GROUP.find((v) => v.value === typeValue)
  return targetObject?.group || ''
}

const transformReportCategoriesForReactSelect = (
  report_caregories: ReportCategory[]
): SelectBoxOption[] => {
  const formatedReportCategories = report_caregories.map((report_caregory) => {
    const formatedItems = report_caregory.account_items.map((account_item) => {
      const formatedItem = {
        value: account_item.id,
        label: String(account_item.name)
      }
      return formatedItem
    })

    const formatedCategory = {
      label: String(report_caregory.category_type),
      options: formatedItems
    }

    return formatedCategory
  })

  return formatedReportCategories
}

const transformReportCategoriesIncomeForReactSelect = (
  report_caregories: ReportCategory[]
): SelectBoxOption[] => {
  const report_caregory_income = report_caregories
    .filter(
      (report_category) =>
        report_category.category_type === CATEGORY_GROUP_NUMBER.INCOME
    )
    .map((report_caregory) => {
      const formatedItems = report_caregory.account_items.map(
        (account_item) => {
          const formatedItem = {
            value: account_item.id,
            label: String(account_item.name)
          }
          return formatedItem
        }
      )

      const formatedCategory = {
        label: typeValue2CategoryGroupName(report_caregory.category_type),
        options: formatedItems
      }

      return formatedCategory
    })

  const report_caregory_debit = report_caregories
    .filter(
      (report_category) =>
        report_category.category_type === CATEGORY_GROUP_NUMBER.DEBIT
    )
    .map((report_caregory) => {
      const formatedItems = report_caregory.account_items.map(
        (account_item) => {
          const formatedItem = {
            value: account_item.id,
            label: String(account_item.name)
          }
          return formatedItem
        }
      )

      const formatedCategory = {
        label: typeValue2CategoryGroupName(report_caregory.category_type),
        options: formatedItems
      }

      return formatedCategory
    })

  return [...report_caregory_income, ...report_caregory_debit]
}

const transformReportCategoriesPaymentForReactSelect = (
  report_caregories: ReportCategory[]
): SelectBoxOption[] => {
  const report_caregory_expense = report_caregories

    .filter(
      (report_category) =>
        report_category.category_type === CATEGORY_GROUP_NUMBER.EXPENSE
    )
    .map((report_caregory) => {
      const formatedItems = report_caregory.account_items.map(
        (account_item) => {
          const formatedItem = {
            value: account_item.id,
            label: String(account_item.name)
          }
          return formatedItem
        }
      )

      const formatedCategory = {
        label: typeValue2CategoryGroupName(report_caregory.category_type),
        options: formatedItems
      }

      return formatedCategory
    })

  const report_caregory_assets = report_caregories
    .filter(
      (report_category) =>
        report_category.category_type === CATEGORY_GROUP_NUMBER.ASSETS
    )
    .map((report_caregory) => {
      const formatedItems = report_caregory.account_items.map(
        (account_item) => {
          const formatedItem = {
            value: account_item.id,
            label: String(account_item.name)
          }
          return formatedItem
        }
      )

      const formatedCategory = {
        label: typeValue2CategoryGroupName(report_caregory.category_type),
        options: formatedItems
      }

      return formatedCategory
    })

  return [...report_caregory_expense, ...report_caregory_assets]
}

// APIの返す値から「プライベートの収入」「プライベートの支出」を省いて返す
const transformReportCategoriesForCreateNew = (
  report_caregories: ReportCategory[]
) => {
  const filterdPrivateReportCategories = report_caregories.map(
    (report_caregory) => {
      const filterd_report_categories = report_caregory.account_items.filter(
        (account_item) =>
          account_item.name !== FILTER_ACCOUNT_ITEM.PRIVATE_INCOME &&
          account_item.name !== FILTER_ACCOUNT_ITEM.PRIVATE_PAYMENT
      )

      return {
        category_type: report_caregory.category_type,
        account_items: filterd_report_categories
      }
    }
  )

  return [...filterdPrivateReportCategories]
}

export const useReportCategories = () => {
  const { apiClient } = useApiClient()

  const { data: report_categories } = useSWRImmutable(
    `/report_categories`,
    async () => {
      try {
        const res = await apiClient.getReportCategories()
        return res.report_categories
      } catch (err) {
        console.error(err)
      }
    }
  )

  const sorted_report_category = reportCategorySorter(report_categories || [])

  // CategorySelectBox用の整形
  const report_categories_for_react_select =
    transformReportCategoriesForReactSelect(sorted_report_category || [])

  // 編集用
  const report_categories_income_for_react_select =
    transformReportCategoriesIncomeForReactSelect(sorted_report_category || [])

  const report_categories_payment_for_react_select =
    transformReportCategoriesPaymentForReactSelect(sorted_report_category || [])

  const report_categories_all_for_react_select = [
    {
      label: undefined,
      options: [
        {
          value: null,
          label: '未選択'
        }
      ]
    },
    ...report_categories_income_for_react_select,
    ...report_categories_payment_for_react_select
  ]

  const report_categories_flatten = sorted_report_category?.flatMap(
    (v) => v.account_items
  )

  return {
    sorted_report_category,
    report_categories,
    report_categories_flatten,
    report_categories_for_react_select,
    report_categories_income_for_react_select,
    report_categories_payment_for_react_select,
    report_categories_all_for_react_select
  }
}
